/**
 * Cookie Consent Context for Baakas
 * Manages user consent preferences for different cookie categories
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getCookie, setCookie, COOKIE_NAMES, COOKIE_EXPIRY } from '../utils/cookies';

// Cookie consent categories
export type ConsentCategory = 'essential' | 'functional' | 'analytics' | 'marketing';

export interface ConsentPreferences {
  essential: boolean; // Always true, cannot be disabled
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
}

interface CookieConsentContextType {
  // Consent state
  hasConsented: boolean;
  preferences: ConsentPreferences;
  showBanner: boolean;
  
  // Actions
  acceptAll: () => void;
  acceptEssentialOnly: () => void;
  updatePreferences: (prefs: Partial<Omit<ConsentPreferences, 'essential' | 'timestamp'>>) => void;
  openSettings: () => void;
  closeSettings: () => void;
  
  // Settings modal state
  isSettingsOpen: boolean;
  
  // Utility hooks
  hasConsentFor: (category: ConsentCategory) => boolean;
}

const defaultPreferences: ConsentPreferences = {
  essential: true, // Always enabled
  functional: true, // Default on for better UX
  analytics: false, // Default off
  marketing: false, // Default off
  timestamp: 0,
};

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preferences, setPreferences] = useState<ConsentPreferences>(defaultPreferences);
  const [hasConsented, setHasConsented] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load consent from cookie on mount
  useEffect(() => {
    const savedConsent = getCookie(COOKIE_NAMES.CONSENT);
    
    if (savedConsent) {
      try {
        const parsed = JSON.parse(savedConsent) as ConsentPreferences;
        // Ensure essential is always true
        parsed.essential = true;
        setPreferences(parsed);
        setHasConsented(true);
        setShowBanner(false);
      } catch {
        // Invalid cookie, show banner
        setShowBanner(true);
      }
    } else {
      // No consent cookie, show banner after a short delay
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1500); // Delay to not interrupt initial page load
      return () => clearTimeout(timer);
    }
    
    setIsInitialized(true);
  }, []);

  // Save consent to cookie
  const saveConsent = useCallback((newPreferences: ConsentPreferences) => {
    const prefsToSave = {
      ...newPreferences,
      essential: true, // Always ensure essential is true
      timestamp: Date.now(),
    };
    
    setCookie(
      COOKIE_NAMES.CONSENT,
      JSON.stringify(prefsToSave),
      { expires: COOKIE_EXPIRY.CONSENT }
    );
    
    setPreferences(prefsToSave);
    setHasConsented(true);
    setShowBanner(false);
    setIsSettingsOpen(false);
    
    // Dispatch event for other parts of the app to react
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { 
      detail: prefsToSave 
    }));
  }, []);

  // Accept all cookies
  const acceptAll = useCallback(() => {
    saveConsent({
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now(),
    });
  }, [saveConsent]);

  // Accept essential only
  const acceptEssentialOnly = useCallback(() => {
    saveConsent({
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
      timestamp: Date.now(),
    });
  }, [saveConsent]);

  // Update specific preferences
  const updatePreferences = useCallback((newPrefs: Partial<Omit<ConsentPreferences, 'essential' | 'timestamp'>>) => {
    saveConsent({
      ...preferences,
      ...newPrefs,
      essential: true,
      timestamp: Date.now(),
    });
  }, [preferences, saveConsent]);

  // Open settings modal
  const openSettings = useCallback(() => {
    setIsSettingsOpen(true);
  }, []);

  // Close settings modal
  const closeSettings = useCallback(() => {
    setIsSettingsOpen(false);
  }, []);

  // Check if user has consent for a specific category
  const hasConsentFor = useCallback((category: ConsentCategory): boolean => {
    if (category === 'essential') return true;
    if (!isInitialized) return false; // Don't allow until initialized
    return preferences[category];
  }, [preferences, isInitialized]);

  const value = useMemo(() => ({
    hasConsented,
    preferences,
    showBanner,
    acceptAll,
    acceptEssentialOnly,
    updatePreferences,
    openSettings,
    closeSettings,
    isSettingsOpen,
    hasConsentFor,
  }), [
    hasConsented,
    preferences,
    showBanner,
    acceptAll,
    acceptEssentialOnly,
    updatePreferences,
    openSettings,
    closeSettings,
    isSettingsOpen,
    hasConsentFor,
  ]);

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

// Hook to use cookie consent context
export const useCookieConsent = (): CookieConsentContextType => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};

// Convenience hook to check consent for a specific category
export const useConsentFor = (category: ConsentCategory): boolean => {
  const { hasConsentFor } = useCookieConsent();
  return hasConsentFor(category);
};

// Hook to check if analytics tracking is allowed
export const useAnalyticsConsent = (): boolean => {
  return useConsentFor('analytics');
};

// Hook to check if marketing/advertising is allowed
export const useMarketingConsent = (): boolean => {
  return useConsentFor('marketing');
};

// Hook to check if functional cookies are allowed
export const useFunctionalConsent = (): boolean => {
  return useConsentFor('functional');
};
